import React, { useEffect, useState, useMemo } from "react";
import { useParams, useSearchParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Loader2, Save, AlertTriangle, CheckCircle2, Copy } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import RichEditor from "@/components/admin/RichEditor";
import api, { readError } from "@/lib/api";

// Hybrid editor: short keys -> plain Input/Textarea; paragraphs -> TipTap RichEditor.
const HEADING_HINTS = ["title", "eyebrow", "subtitle", "tagline", "label", "filter_", "form_", "name", "button", "cta"];
const PARAGRAPH_HINTS_REGEX = /^p\d+$|_body$|_p\d+$/;

function classifyKey(key, value) {
  if (Array.isArray(value)) return "list";
  if (value && typeof value === "object") return "list";
  const k = key.toLowerCase();
  if (PARAGRAPH_HINTS_REGEX.test(k) || k === "intro_p1" || k === "intro_p2") return "rich";
  if (HEADING_HINTS.some((h) => k.includes(h))) return "short";
  if (typeof value === "string" && value.length > 200) return "rich";
  return "short";
}

// Strip simple HTML for word-count display & to compare against original
function stripHtml(html) {
  if (typeof html !== "string") return "";
  return html.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
}

function countWords(value) {
  if (Array.isArray(value)) {
    return value.reduce((acc, item) => {
      if (typeof item === "string") return acc + item.split(/\s+/).filter(Boolean).length;
      if (item && typeof item === "object") {
        return acc + Object.values(item).filter((v) => typeof v === "string").reduce((s, v) => s + v.split(/\s+/).filter(Boolean).length, 0);
      }
      return acc;
    }, 0);
  }
  if (typeof value !== "string") return 0;
  return stripHtml(value).split(/\s+/).filter(Boolean).length;
}

export default function AdminPageEditor() {
  const { slug } = useParams();
  const [params] = useSearchParams();
  const locale = params.get("locale") || "hr";
  const navigate = useNavigate();

  const [page, setPage] = useState(null);
  const [content, setContent] = useState({});
  const [meta, setMeta] = useState({ meta_title: "", meta_description: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [copying, setCopying] = useState(false);

  useEffect(() => {
    setPage(null);
    api.get(`/admin/pages/${slug}`, { params: { locale } })
      .then((r) => {
        setPage(r.data);
        setContent(r.data.content || {});
        setMeta({ meta_title: r.data.meta_title || "", meta_description: r.data.meta_description || "" });
      })
      .catch((err) => setError(readError(err, "Could not load page.")));
  }, [slug, locale]);

  const sectionKeys = useMemo(() => Object.keys(content), [content]);

  const updateField = (key, val) => setContent((c) => ({ ...c, [key]: val }));

  // For list-type values, render simple textarea-per-row editor (preserves order).
  const updateListItem = (key, idx, field, value) => {
    setContent((c) => {
      const arr = Array.isArray(c[key]) ? [...c[key]] : [];
      if (typeof arr[idx] === "object" && arr[idx] !== null) {
        arr[idx] = { ...arr[idx], [field]: value };
      } else {
        arr[idx] = value;
      }
      return { ...c, [key]: arr };
    });
  };

  const save = async () => {
    setBusy(true);
    setError("");
    try {
      const { data } = await api.put(`/admin/pages/${slug}`, {
        meta_title: meta.meta_title,
        meta_description: meta.meta_description,
        content,
      }, { params: { locale } });
      setPage(data);
      toast.success(`Page "${slug}" saved (${locale.toUpperCase()})`);
    } catch (err) {
      setError(readError(err, "Could not save page."));
    } finally {
      setBusy(false);
    }
  };

  const copyToOthers = async () => {
    if (locale !== "hr") {
      toast.error("Copy is only available from the HR (source) version.");
      return;
    }
    setCopying(true);
    try {
      await api.post(`/admin/pages/${slug}/copy-locales`, { target_locales: ["en", "de", "fr"] }, { params: { source_locale: "hr" } });
      toast.success("Content copied to EN / DE / FR (marked auto-copied).");
    } catch (err) {
      toast.error(readError(err, "Copy failed."));
    } finally {
      setCopying(false);
    }
  };

  if (!page && !error) {
    return (
      <div className="p-10 flex items-center justify-center text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading…
      </div>
    );
  }

  const totalWords = sectionKeys.reduce((acc, k) => acc + countWords(content[k]), 0);

  return (
    <div className="space-y-8 animate-fade-up">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <Link to="/admin/pages" data-testid="back-to-pages" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary mb-3">
            <ArrowLeft className="h-3.5 w-3.5" strokeWidth={1.5} /> All pages
          </Link>
          <div className="text-[10px] uppercase tracking-[0.25em] text-primary">{locale.toUpperCase()} · {sectionKeys.length} sections · {totalWords} words</div>
          <h1 className="mt-1 font-serif text-4xl text-foreground">{slug}</h1>
        </div>
        <div className="flex items-center gap-2">
          {locale === "hr" && (
            <Button variant="outline" data-testid="copy-locales-btn" onClick={copyToOthers} disabled={copying || busy}>
              {copying ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Copy className="h-3.5 w-3.5" strokeWidth={1.5} />}
              Copy HR → EN/DE/FR
            </Button>
          )}
          <Button data-testid="save-page-btn" onClick={save} disabled={busy} className="shadow-interactive">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" strokeWidth={1.5} />}
            Save changes
          </Button>
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-destructive/40 bg-destructive/10 p-4 text-sm text-destructive flex items-start gap-2">
          <AlertTriangle className="h-4 w-4 mt-0.5" strokeWidth={1.5} />
          {error}
        </div>
      )}

      {/* SEO meta */}
      <Card className="bg-card border-border/60 shadow-ambient">
        <CardContent className="p-6 space-y-4">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="h-4 w-4 text-primary" strokeWidth={1.5} />
            <h2 className="font-serif text-lg">SEO metadata</h2>
          </div>
          <div className="space-y-2">
            <Label htmlFor="mt">Meta title</Label>
            <Input id="mt" data-testid="meta-title-input" value={meta.meta_title} onChange={(e) => setMeta((m) => ({ ...m, meta_title: e.target.value }))} className="h-11 bg-background" />
            <p className="text-xs text-muted-foreground">{(meta.meta_title || "").length}/60 chars</p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="md">Meta description</Label>
            <Textarea id="md" data-testid="meta-desc-input" value={meta.meta_description} onChange={(e) => setMeta((m) => ({ ...m, meta_description: e.target.value }))} rows={3} className="bg-background resize-none" />
            <p className="text-xs text-muted-foreground">{(meta.meta_description || "").length}/160 chars</p>
          </div>
        </CardContent>
      </Card>

      {/* Sections editor */}
      <div className="space-y-5" data-testid="sections-editor">
        {sectionKeys.map((key) => {
          const value = content[key];
          const kind = classifyKey(key, value);
          const wc = countWords(value);
          return (
            <Card key={key} className="bg-card border-border/60 shadow-ambient">
              <CardContent className="p-5 space-y-3">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">{kind}</div>
                    <div className="mt-0.5 font-mono text-sm text-foreground">{key}</div>
                  </div>
                  <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">{wc} words</div>
                </div>

                {kind === "short" && (
                  <Input
                    value={value || ""}
                    onChange={(e) => updateField(key, e.target.value)}
                    className="h-11 bg-background"
                    data-testid={`field-${key}`}
                  />
                )}
                {kind === "rich" && (
                  <RichEditor
                    value={value || ""}
                    onChange={(html) => updateField(key, html)}
                    testid={`rich-${key}`}
                  />
                )}
                {kind === "list" && Array.isArray(value) && (
                  <div className="space-y-3" data-testid={`list-${key}`}>
                    {value.map((item, idx) => (
                      <div key={idx} className="rounded-lg border border-border/60 p-3 bg-background">
                        {typeof item === "string" ? (
                          <Textarea
                            value={item}
                            onChange={(e) => updateListItem(key, idx, "", e.target.value)}
                            rows={2}
                            className="bg-background resize-none"
                          />
                        ) : (
                          <div className="space-y-2">
                            {Object.entries(item).map(([f, v]) => (
                              <div key={f} className="space-y-1">
                                <Label className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">{f}</Label>
                                {(v || "").length > 80 ? (
                                  <Textarea value={v} onChange={(e) => updateListItem(key, idx, f, e.target.value)} rows={3} className="bg-background resize-none" />
                                ) : (
                                  <Input value={v || ""} onChange={(e) => updateListItem(key, idx, f, e.target.value)} className="h-10 bg-background" />
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      <div className="pt-2">
        <Button data-testid="save-page-btn-bottom" onClick={save} disabled={busy} className="h-11 shadow-interactive">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" strokeWidth={1.5} />}
          Save changes
        </Button>
      </div>
    </div>
  );
}
